'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  CHANNEL_SYNC_ROWS,
  CHANNEL_SYNC_STATS,
  MENU_ITEMS,
  STORE_SYNC_ROWS,
  STORE_SYNC_STATS,
  type ChannelStatus,
  type MenuItem,
  type StoreStatus,
} from '../data/central-menu-ui-data'
import type { UiStrings } from './central-menu-ui.types'
import { Measure } from '@/components/ui/measure'
import { Col } from '@/components/ui/col'

type Mode = 'store' | 'channel'
type UnifiedStatus = StoreStatus | ChannelStatus

const NAV_KEYS = ['nav1', 'nav2', 'nav3', 'nav4'] as const
const BADGE_TONE_CLASS: Record<string, string> = {
  grab: 'bg-grab',
  shopee: 'bg-shopee',
  be: 'bg-be text-charcoal-900',
  momo: 'bg-momo',
  zalo: 'bg-zalo',
  'brand-red': 'bg-brand-red',
}

function StatusIcon({ status }: { status: UnifiedStatus }) {
  if (status === 'ok') {
    return (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
      </svg>
    )
  }
  if (status === 'pending') {
    return (
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-500" />
      </span>
    )
  }
  if (status === 'err2') {
    return (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    )
  }
  return (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function statusClass(status: UnifiedStatus) {
  if (status === 'ok') return 'text-green-700 bg-green-50 border-green-200'
  if (status === 'pending') return 'text-yellow-700 bg-white border-yellow-300'
  return 'text-brand-red bg-white border-brand-red/30'
}

function MenuItemSelector({
  items,
  selected,
  onSelect,
  mode,
}: {
  items: readonly MenuItem[]
  selected: MenuItem
  onSelect: (item: MenuItem) => void
  mode: Mode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isStore = mode === 'store'
  const focusRing = isStore
    ? 'focus:border-brand-red focus:ring-brand-red'
    : 'focus:border-blue-600 focus:ring-blue-600'
  const accentText = isStore ? 'text-brand-red' : 'text-blue-600'

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-[220px] cursor-pointer rounded-md border border-gray-300 bg-white py-1 pl-3 pr-8 text-left text-xl font-bold text-charcoal-900 shadow-sm focus:outline-none focus:ring-2 ${focusRing}`}
      >
        {selected.label}
      </button>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-charcoal-600">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5">
          <div className="py-1">
            {items.map((item) => {
              const isActive = item.value === selected.value
              return (
                <button
                  key={item.value}
                  onClick={() => {
                    onSelect(item)
                    setOpen(false)
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isActive ? `bg-gray-50 font-bold ${accentText}` : 'text-charcoal-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex-1 text-sm">{item.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function Sidebar({ strings }: { strings: UiStrings }) {
  return (
    <div className="w-64 flex-shrink-0 bg-charcoal-900">
      <div className="p-6">
        <div className="mb-8 flex items-center gap-2">
          <div className="h-4 w-4 rounded-sm bg-brand-red" />
          <span className="text-base font-bold tracking-wide text-white">DCORP</span>
        </div>
        <div className="mb-3 font-mono text-fs-sm uppercase tracking-widest text-charcoal-500">
          {strings.modules}
        </div>
        <ul className="space-y-2">
          {NAV_KEYS.map((navKey, idx) => {
            const active = idx === 1
            return (
              <li
                key={navKey}
                className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                  active ? 'bg-charcoal-800 text-white font-medium py-2' : 'text-gray-400 hover:text-white font-light'
                }`}
              >
                <div
                  className={`h-1.5 w-1.5 rounded-full ${
                    active ? 'bg-brand-red shadow-[0_0_5px_rgba(179,0,11,0.8)]' : 'bg-charcoal-600'
                  }`}
                />
                <span>{strings[navKey]}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function MockPageHeader({
  strings,
  mode,
  selectedItem,
  onSelect,
}: {
  strings: UiStrings
  mode: Mode
  selectedItem: MenuItem
  onSelect: (item: MenuItem) => void
}) {
  const isStore = mode === 'store'

  return (
    <div className="mb-6 flex items-end justify-between border-b border-gray-200 pb-5">
      <div className="flex items-start gap-4">
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 shadow-sm">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={selectedItem.image}
            alt=""
            className="h-full w-full object-cover transition-all duration-300"
          />
        </div>
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            <MenuItemSelector
              items={MENU_ITEMS}
              selected={selectedItem}
              onSelect={onSelect}
              mode={mode}
            />
          </div>
          <div className="flex items-center gap-4">
            <p className={`rounded border border-gray-200 px-2 py-0.5 font-mono text-sm text-charcoal-500 ${isStore ? 'bg-gray-100' : 'bg-white shadow-sm'}`}>
              {strings.sku_label}: {selectedItem.sku}
            </p>
            <p className="flex items-center gap-1 text-sm text-charcoal-500">
              <span className={`h-1.5 w-1.5 rounded-full ${isStore ? 'bg-charcoal-400' : 'bg-blue-500'}`} />
              {isStore ? strings.scope_store : strings.scope_channel}
            </p>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <button className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-xs font-bold text-charcoal-900 shadow-sm transition-colors hover:bg-gray-50">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>{strings.btn_refresh}</span>
        </button>
        <button
          className={`rounded-lg border border-transparent px-5 py-2.5 text-xs font-bold text-white shadow-sm transition-colors ${
            isStore ? 'bg-brand-red hover:bg-brand-darkRed' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isStore ? strings.btn_retry1 : strings.btn_retry2}
        </button>
      </div>
    </div>
  )
}

function StatPill({
  value,
  label,
  tone,
  neutralSurface = 'gray',
}: {
  value: string
  label: string
  tone: 'neutral' | 'success' | 'error' | 'pending'
  neutralSurface?: 'gray' | 'white'
}) {
  const cfg =
    tone === 'success'
      ? {
          wrapper: 'bg-green-50 border-green-200',
          dot: 'bg-green-500',
          value: 'text-green-700',
          label: 'text-green-600',
        }
      : tone === 'error'
        ? {
            wrapper: 'bg-red-50 border-red-200',
            dot: 'bg-brand-red',
            value: 'text-brand-red',
            label: 'text-red-600',
          }
        : tone === 'pending'
          ? {
              wrapper: 'bg-yellow-50 border-yellow-200',
              dot: 'bg-yellow-500',
              value: 'text-yellow-700',
              label: 'text-yellow-600',
            }
          : {
              wrapper: `${neutralSurface === 'white' ? 'bg-white' : 'bg-gray-50'} border-gray-200`,
              value: 'text-charcoal-900',
              label: 'text-charcoal-500',
            }

  return (
    <div
      className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm ${cfg.wrapper} ${
        tone === 'neutral' ? 'shadow-sm' : ''
      }`}
    >
      {tone !== 'neutral' && <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />}
      <span className={`font-semibold ${cfg.value}`}>{value}</span>
      <span className={cfg.label}>{label}</span>
    </div>
  )
}

function statusBadgeClass(status: UnifiedStatus) {
  const gap = status === 'pending' ? 'gap-2' : 'gap-1.5'
  const shadow = status === 'ok' ? '' : 'shadow-sm'
  return `flex items-center ${gap} rounded border px-2.5 py-1 text-fs-md font-medium ${statusClass(status)} ${shadow}`
}

export function CentralMenuUiMockup({ strings }: { strings: UiStrings }) {
  const [mode, setMode] = useState<Mode>('store')
  const [selectedItem, setSelectedItem] = useState<MenuItem>(MENU_ITEMS[0])

  useEffect(() => {
    const timer = setInterval(() => {
      setMode((prev) => (prev === 'store' ? 'channel' : 'store'))
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  const modeClass = useMemo(
    () => ({
      store:
        mode === 'store'
          ? 'bg-charcoal-900 text-white shadow-md active:scale-95'
          : 'bg-transparent text-charcoal-600 hover:text-charcoal-900 active:scale-95',
      channel:
        mode === 'channel'
          ? 'bg-blue-600 text-white shadow-md active:scale-95'
          : 'bg-transparent text-charcoal-600 hover:text-charcoal-900 active:scale-95',
    }),
    [mode],
  )

  return (
    <>
      <div className="relative z-20 mx-auto mb-8 flex w-fit gap-2 rounded-xl border border-gray-300 bg-gray-200 p-1.5 shadow-sm">
        <Button
          variant="mockupToggle"
          size="mockupToggle"
          onClick={() => setMode('store')}
          className={modeClass.store}
        >
          {strings.btn_store}
        </Button>
        <Button
          variant="mockupToggle"
          size="mockupToggle"
          onClick={() => setMode('channel')}
          className={modeClass.channel}
        >
          {strings.btn_channel}
        </Button>
      </div>

      <Measure size="5xl" className="w-full">
      <Col className="central-menu-mockup ui-mockup-shadow ui-mockup-shadow-central-pos flex h-[750px] w-full overflow-hidden rounded-xl bg-white">
        <div className="relative z-20 flex h-10 flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-gray-100 px-4">
          <div className="h-3 w-3 rounded-full bg-gray-300" />
          <div className="h-3 w-3 rounded-full bg-gray-300" />
          <div className="h-3 w-3 rounded-full bg-gray-300" />
          <div className="ml-4 flex flex-grow justify-center">
            <div className="flex h-6 w-96 items-center rounded-md border border-gray-200 bg-white px-3 shadow-sm">
              <span className="mt-0.5 font-mono text-fs-sm text-gray-400">
                hq.dcorp.platform/menu-master/sync-status/{selectedItem.sku}
              </span>
            </div>
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden bg-white">
          <div
            className="flex h-full w-[200%] transition-transform duration-700 ease-in-out"
            style={{ transform: mode === 'store' ? 'translateX(0%)' : 'translateX(-50%)' }}
          >
            <div className="flex h-full w-1/2">
              <Sidebar strings={strings} />
              <Col className="flex flex-1 bg-white p-8">
                <div className="mb-4 flex items-center gap-2 font-mono text-fs-md uppercase tracking-wider text-charcoal-400">
                  <span className="cursor-pointer transition-colors hover:text-brand-red">{strings.menu_master}</span>
                  <span>/</span>
                  <span className="font-semibold text-charcoal-900">{strings.bc_store}</span>
                </div>
                <MockPageHeader strings={strings} mode="store" selectedItem={selectedItem} onSelect={setSelectedItem} />
                <div className="mb-4 flex gap-4">
                  {STORE_SYNC_STATS.map((stat) => (
                    <StatPill key={`${stat.value}-${stat.labelKey}`} value={stat.value} label={strings[stat.labelKey]} tone={stat.tone} />
                  ))}
                </div>
                <Col className="flex flex-1 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                  <div className="grid grid-cols-12 border-b border-charcoal-800 bg-charcoal-900 p-3.5 font-mono text-fs-md font-semibold uppercase tracking-widest text-white/90">
                    <div className="col-span-2 pl-2">{strings.th1}</div>
                    <div className="col-span-4">{strings.th2}</div>
                    <div className="col-span-3">{strings.th3}</div>
                    <div className="col-span-3 pr-2 text-right">{strings.th4}</div>
                  </div>
                  <div className="mock-scroll flex-1 overflow-y-auto bg-white">
                    {STORE_SYNC_ROWS.map((row) => {
                      const rowClass =
                        row.status === 'ok'
                          ? 'border-b border-gray-100 hover:bg-gray-50 cursor-pointer'
                          : row.status === 'pending'
                            ? 'border-b border-yellow-100 border-l-4 border-l-yellow-400 bg-yellow-50/30 hover:bg-yellow-50/70 cursor-pointer'
                            : 'border-b border-red-100 border-l-4 border-l-brand-red bg-red-50/50 hover:bg-red-50 cursor-pointer'
                      const codeClass =
                        row.status === 'ok'
                          ? 'text-charcoal-500'
                          : row.status === 'pending'
                            ? 'font-bold text-yellow-700'
                            : 'font-bold text-brand-red'
                      const codePad = row.status === 'ok' ? 'pl-2' : 'pl-1'
                      const timeClass =
                        row.status === 'ok'
                          ? 'text-charcoal-500'
                          : row.status === 'pending'
                            ? 'text-charcoal-400'
                            : 'font-medium text-brand-red'

                      return (
                      <div key={row.code} className={`grid grid-cols-12 items-center p-3.5 text-sm ${rowClass}`}>
                        <div className={`col-span-2 ${codePad} font-mono text-xs ${codeClass}`}>{row.code}</div>
                        <div className="col-span-4 font-semibold text-charcoal-900">{strings[row.nameKey]}</div>
                        <div className="col-span-3">
                          <span className={statusBadgeClass(row.status)}>
                            <StatusIcon status={row.status} />
                            <span>{strings[row.statusLabelKey]}</span>
                          </span>
                        </div>
                        <div className={`col-span-3 pr-2 text-right font-mono text-xs ${timeClass}`}>
                          {row.time ?? strings[row.timeKey ?? 's_pend1']}
                        </div>
                      </div>
                    )})}
                  </div>
                </Col>
              </Col>
            </div>

            <div className="flex h-full w-1/2 bg-[#f8fafc]">
              <Sidebar strings={strings} />
              <Col className="flex flex-1 p-8">
                <div className="mb-4 flex items-center gap-2 font-mono text-fs-md uppercase tracking-wider text-charcoal-400">
                  <span className="cursor-pointer transition-colors hover:text-brand-red">{strings.menu_master}</span>
                  <span>/</span>
                  <span className="font-semibold text-charcoal-900">{strings.bc_channel}</span>
                </div>
                <MockPageHeader strings={strings} mode="channel" selectedItem={selectedItem} onSelect={setSelectedItem} />
                <div className="mb-4 flex gap-4">
                  {CHANNEL_SYNC_STATS.map((stat) => (
                    <StatPill
                      key={`${stat.value}-${stat.labelKey}`}
                      value={stat.value}
                      label={strings[stat.labelKey]}
                      tone={stat.tone}
                      neutralSurface="white"
                    />
                  ))}
                </div>
                <Col className="flex flex-1 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                  <div className="grid grid-cols-12 border-b border-gray-200 bg-gray-100 p-3.5 font-mono text-fs-md font-bold uppercase tracking-widest text-charcoal-600">
                    <div className="col-span-3 pl-2">{strings.th1c}</div>
                    <div className="col-span-4">{strings.th2c}</div>
                    <div className="col-span-3">{strings.th3}</div>
                    <div className="col-span-2 pr-2 text-right">{strings.th4}</div>
                  </div>
                  <div className="mock-scroll flex-1 overflow-y-auto">
                    {CHANNEL_SYNC_ROWS.map((row) => {
                      const rowClass =
                        row.status === 'ok'
                          ? 'border-b border-gray-100 hover:bg-gray-50 transition-colors'
                          : row.status === 'pending'
                            ? 'border-b border-yellow-100 border-l-4 border-l-yellow-400 bg-yellow-50/40 hover:bg-yellow-50/80 transition-colors'
                            : 'border-b border-red-100 border-l-4 border-l-brand-red bg-red-50/50 hover:bg-red-50 transition-colors'
                      const leftPad = row.status === 'ok' ? 'pl-2' : 'pl-1'
                      const codeTone =
                        row.status === 'ok'
                          ? 'text-charcoal-500 font-medium'
                          : row.status === 'pending'
                            ? 'text-yellow-700 font-bold'
                            : 'text-brand-red font-bold'
                      const timeTone =
                        row.status === 'ok'
                          ? 'text-charcoal-500'
                          : row.status === 'pending'
                            ? 'text-charcoal-400'
                            : 'text-brand-red font-medium'

                      return (
                        <div key={row.code} className={`grid grid-cols-12 items-center p-4 text-sm ${rowClass}`}>
                          <div className={`col-span-3 flex items-center gap-3 ${leftPad}`}>
                            <div
                              className={`flex h-7 w-7 items-center justify-center rounded-md text-fs-md font-bold text-white shadow-sm ${
                                BADGE_TONE_CLASS[row.badgeTone] ?? 'bg-charcoal-700'
                              } ${
                                row.badgeTone === 'be'
                                  ? 'text-fs-lg font-black'
                                  : row.badgeTone === 'zalo'
                                    ? 'text-fs-lg'
                                    : ''
                              }`}
                            >
                              {row.badge}
                            </div>
                            <span className={`font-mono text-xs ${codeTone}`}>{row.code}</span>
                          </div>
                          <div className="col-span-4 font-bold text-charcoal-900">{row.name ?? strings[row.nameKey ?? 'c1']}</div>
                          <div className="col-span-3 flex items-center gap-2">
                            <span className={statusBadgeClass(row.status)}>
                              <StatusIcon status={row.status} />
                              <span>{strings[row.statusLabelKey]}</span>
                            </span>
                          </div>
                          <div className={`col-span-2 pr-2 text-right font-mono text-fs-md ${timeTone}`}>
                            {row.time ?? strings[row.timeKey ?? 's_pend4']}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </Col>
              </Col>
            </div>
          </div>
        </div>
      </Col>
      </Measure>
    </>
  )
}
